import { Minus, X } from "lucide-react";

import type { ScoreValue } from "@/infrastructure/supabase/database.types";

interface ScorePickerProps {
  value: ScoreValue | null;
  dealbreakerFail: boolean;
  dealbreakerEligible: boolean;
  accentColor: string;
  onChange: (next: { score: ScoreValue | null; dealbreakerFail: boolean }) => void;
}

const SCORE_CELLS: ScoreValue[] = [0, 1, 2, 3, 4, 5];

export function ScorePicker({
  value,
  dealbreakerFail,
  dealbreakerEligible,
  accentColor,
  onChange,
}: ScorePickerProps) {
  const neutralBorder = "oklch(0.91 0.008 240)";
  const dealbreakerColor = "oklch(0.62 0.18 25)";

  const isClear = !dealbreakerFail && value === null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <PickerCell
        active={isClear}
        accent="oklch(0.55 0.01 240)"
        neutralBorder={neutralBorder}
        onClick={() => onChange({ score: null, dealbreakerFail: false })}
        ariaLabel="Not evaluated"
      >
        <Minus className="size-3.5" />
      </PickerCell>

      {SCORE_CELLS.map((s) => {
        const active = !dealbreakerFail && value === s;
        return (
          <PickerCell
            key={s}
            active={active}
            accent={accentColor}
            neutralBorder={neutralBorder}
            onClick={() => onChange({ score: s, dealbreakerFail: false })}
            ariaLabel={`Score ${s}`}
          >
            <span className="font-data text-[13px] font-medium tabular-nums">{s}</span>
          </PickerCell>
        );
      })}

      {dealbreakerEligible ? (
        <PickerCell
          active={dealbreakerFail}
          accent={dealbreakerColor}
          neutralBorder={neutralBorder}
          onClick={() => onChange({ score: null, dealbreakerFail: true })}
          ariaLabel="Dealbreaker fail"
        >
          <X className="size-3.5" />
        </PickerCell>
      ) : null}
    </div>
  );
}

function PickerCell({
  active,
  accent,
  neutralBorder,
  onClick,
  ariaLabel,
  children,
}: {
  active: boolean;
  accent: string;
  neutralBorder: string;
  onClick: () => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={active}
      className="inline-flex size-8 items-center justify-center rounded-md border text-[12px] font-medium transition-all"
      style={
        active
          ? { borderColor: accent, background: `${accent}1a`, color: accent }
          : { borderColor: neutralBorder, color: "oklch(0.55 0.01 240)" }
      }
    >
      {children}
    </button>
  );
}
